import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { LayoutDashboard, Calendar, Users, Receipt, ShieldCheck, LogOut, Home as HomeIcon, UserCog, TrendingUp, Megaphone, Shield, Menu, X, ClipboardList, FileText, LayoutTemplate, Handshake, LayoutGrid, FileEdit, Mail, Target, BarChart3, Radio, ClipboardEdit, Trophy, Upload, History, ShieldAlert, MessageSquare, UserPlus, Activity, Building2, Users2, GraduationCap, Database, CreditCard, ShoppingBag, Flag } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "admin-nav-overview" },
  { to: "/admin/camps", label: "Camps", icon: Calendar, testid: "admin-nav-camps" },
  { to: "/admin/players", label: "Players", icon: ShieldCheck, testid: "admin-nav-players" },
  { to: "/admin/schools", label: "Schools", icon: GraduationCap, testid: "admin-nav-schools" },
  { to: "/admin/unclaimed-players", label: "Unclaimed Players", icon: UserPlus, testid: "admin-nav-unclaimed" },
  { to: "/evaluator/dashboard", label: "Evaluator Dashboard", icon: ClipboardList, testid: "admin-nav-evaluator-dashboard" },
  { group: "Clubs & Teams" },
  { to: "/admin/live/organizations", label: "Organizations", icon: Building2, testid: "admin-nav-organizations" },
  { to: "/admin/live/teams", label: "Teams", icon: Users2, testid: "admin-nav-live-teams" },
  { to: "/admin/teams", label: "CBR Nation", icon: Shield, testid: "admin-nav-teams" },
  { to: "/admin/interests", label: "Player Interests", icon: Trophy, testid: "admin-nav-interests" },
  { to: "/admin/wwba", label: "WWBA Registrations", icon: Trophy, testid: "admin-nav-wwba" },
  { group: "CBR Live" },
  { to: "/admin/live/games", label: "Live Games & Scoring", icon: Radio, testid: "admin-nav-live-games" },
  { to: "/admin/games", label: "Games Master List", icon: Database, testid: "admin-nav-games-master" },
  { to: "/admin/games/approval", label: "Approval Queue", icon: ClipboardEdit, testid: "admin-nav-approval-queue" },
  { to: "/admin/games/corrections", label: "Correction Requests", icon: Flag, testid: "admin-nav-corrections" },
  { to: "/admin/game-stats", label: "Game Stats & Tournaments", icon: ClipboardList, testid: "admin-nav-game-stats" },
  { to: "/admin/live/pitch-counts", label: "Pitch Count Safety", icon: ShieldAlert, testid: "admin-nav-pitch-counts" },
  { to: "/admin/feedback", label: "Beta Feedback", icon: MessageSquare, testid: "admin-nav-feedback" },
  { to: "/admin/live/claims", label: "Ownership Claims", icon: ShieldCheck, testid: "admin-nav-live-claims" },
  { to: "/admin/live/manual-entry", label: "Manual Entry", icon: ClipboardEdit, testid: "admin-nav-live-manual" },
  { to: "/admin/live/leagues", label: "Leagues", icon: Trophy, testid: "admin-nav-live-leagues" },
  { to: "/admin/live/import", label: "Bulk Import", icon: Upload, testid: "admin-nav-live-import" },
  { to: "/admin/live/import-history", label: "Import History", icon: History, testid: "admin-nav-live-import-history" },
  { group: "General" },
  { to: "/admin/recruiters", label: "Recruiters", icon: Target, testid: "admin-nav-recruiters" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testid: "admin-nav-analytics" },
  { to: "/admin/health", label: "System Health", icon: Activity, testid: "admin-nav-health" },
  { to: "/admin/rankings", label: "Rankings", icon: TrendingUp, testid: "admin-nav-rankings" },
  { to: "/admin/ingame-rankings", label: "In-Game Rankings", icon: TrendingUp, testid: "admin-nav-ingame-rankings" },
  { to: "/admin/social", label: "Media Engine", icon: Megaphone, testid: "admin-nav-social" },
  { to: "/admin/cms", label: "Content", icon: FileText, testid: "admin-nav-cms" },
  { to: "/admin/site-content", label: "Site Content", icon: FileEdit, testid: "admin-nav-site-content" },
  { to: "/admin/homepage", label: "Homepage", icon: LayoutTemplate, testid: "admin-nav-homepage" },
  { to: "/admin/media-wall", label: "Media Wall", icon: LayoutGrid, testid: "admin-nav-media-wall" },
  { to: "/admin/sponsors", label: "Sponsors", icon: Handshake, testid: "admin-nav-sponsors" },
  { to: "/admin/sponsorship", label: "Sponsor Sales", icon: Handshake, testid: "admin-nav-sponsorship" },
  { to: "/admin/registrations", label: "Registrations", icon: Users, testid: "admin-nav-registrations" },
  { to: "/admin/payments", label: "Payments", icon: Receipt, testid: "admin-nav-payments" },
  { to: "/admin/payment-test", label: "Payment Test", icon: CreditCard, testid: "admin-nav-payment-test" },
  { to: "/admin/marketplace", label: "Marketplace", icon: ShoppingBag, testid: "admin-nav-marketplace" },
  { to: "/admin/email-log", label: "Email Log", icon: Mail, testid: "admin-nav-email-log" },
  { to: "/admin/users", label: "Users", icon: UserCog, testid: "admin-nav-users" },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadFeedback, setUnreadFeedback] = useState(0);
  const [pendingRecruiters, setPendingRecruiters] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);

  useEffect(() => {
    let active = true;
    api.get("/feedback", { params: { status: "new" } })
      .then((res) => { if (active) setUnreadFeedback(res.data?.counts?.new || 0); })
      .catch(() => {});
    api.get("/live/admin/games/approval-badges")
      .then((res) => { if (active) { const d = res.data || {}; setPendingApprovals((d.pending_cbr || 0) + (d.correction_required || 0)); } })
      .catch(() => {});
    const onApprovalChanged = () => api.get("/live/admin/games/approval-badges").then((res) => { const d = res.data || {}; setPendingApprovals((d.pending_cbr || 0) + (d.correction_required || 0)); }).catch(() => {});
    window.addEventListener("cbr:approvals-changed", onApprovalChanged);
    const loadRecruiterCount = () => api.get("/admin/recruiter-requests/count")
      .then((res) => { if (active) setPendingRecruiters(res.data?.count || 0); })
      .catch(() => {});
    loadRecruiterCount();
    const onChanged = () => loadRecruiterCount();
    window.addEventListener("cbr:recruiter-requests-changed", onChanged);
    return () => { active = false; window.removeEventListener("cbr:recruiter-requests-changed", onChanged); window.removeEventListener("cbr:approvals-changed", onApprovalChanged); };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-[#0A192F] text-white px-4 py-3">
        <div className="flex items-center gap-2">
          <img src="/cbr-logo.png" alt="CBR Prospects" className="h-6 w-auto" />
          <span className="font-display text-sm uppercase tracking-tight border-l border-white/20 pl-2">Admin</span>
        </div>
        <button data-testid="admin-mobile-menu-toggle" onClick={() => setOpen(true)} className="p-2 -mr-2">
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile backdrop */}
      {open && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setOpen(false)} />}

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`w-64 bg-[#0A192F] text-white flex flex-col fixed h-screen z-50 transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="px-6 py-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/cbr-logo.png" alt="CBR Prospects" className="h-7 w-auto" />
            <div className="border-l border-white/20 pl-2">
              <div className="font-display text-sm uppercase tracking-tight">Admin</div>
              <div className="label-overline text-white/40 text-[9px]">Control Room</div>
            </div>
          </div>
          <button data-testid="admin-sidebar-close" onClick={() => setOpen(false)} className="lg:hidden p-1 text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            n.group ? (
              <div key={`group-${n.group}`} className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-white/30">{n.group}</div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm ${
                    isActive ? "bg-[#E63946] text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <n.icon className="w-4 h-4" /> {n.label}
                {n.testid === "admin-nav-feedback" && unreadFeedback > 0 && (
                  <span
                    data-testid="admin-feedback-unread-badge"
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#E63946] text-white text-[10px] font-bold"
                  >
                    {unreadFeedback > 99 ? "99+" : unreadFeedback}
                  </span>
                )}
                {n.testid === "admin-nav-approval-queue" && pendingApprovals > 0 && (
                  <span
                    data-testid="admin-approval-pending-badge"
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#E63946] text-white text-[10px] font-bold"
                  >
                    {pendingApprovals > 99 ? "99+" : pendingApprovals}
                  </span>
                )}
                {n.testid === "admin-nav-recruiters" && pendingRecruiters > 0 && (
                  <span
                    data-testid="admin-recruiter-pending-badge"
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#E63946] text-white text-[10px] font-bold"
                  >
                    {pendingRecruiters > 99 ? "99+" : pendingRecruiters}
                  </span>
                )}
              </NavLink>
            )
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <div className="px-3 py-2">
            <div className="label-overline text-white/40">Signed in</div>
            <div className="text-sm font-semibold mt-1 truncate">{user?.email}</div>
            <div className="text-[10px] text-white/50 uppercase tracking-wider mt-0.5">{user?.role}</div>
          </div>
          <NavLink to="/" className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-white/60 hover:text-white">
            <HomeIcon className="w-4 h-4" /> View Public Site
          </NavLink>
          <button
            data-testid="admin-logout-button"
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-[#E63946] hover:text-white w-full text-left"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 min-h-screen">{children}</main>
    </div>
  );
}
